import { ConfirmationDialog } from '@common/components/confirmation-dialog';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirtyContext } from '../dirty-provider';
import { DataLossContext, DataLossHandler } from './data-loss-context';

export type DataLossProviderProps = PropsWithChildren;

/**
 * DataLossProvider is a React functional component that manages a context for handling
 * potential data loss scenarios, such as unsaved changes when navigating away from a page.
 *
 * This component provides a context (`DataLossContext`) to its children, enabling them to
 * set and handle potential data loss confirmation callbacks. It integrates with a `useDirtyContext`
 * hook to identify whether there are unsaved changes and ensures proper confirmation prompts.
 *
 * Features:
 * - Enables confirmation dialogs to prompt users when unsaved changes exist before they proceed.
 * - Provides methods to set and manage data loss handlers.
 * - Integrates with the `useTranslation` hook for localized messaging.
 *
 * Props:
 * - children: ReactNode - The content rendered within the provider.
 *
 * Context Behavior:
 * - Provides a `handler` property representing the current data loss handling callback.
 * - Provides a `setHandler` function to configure the handler dynamically.
 */
export const DataLossProvider: FC<DataLossProviderProps> = ({ children }) => {
  const [t] = useTranslation();
  const dirtyContext = useDirtyContext();
  const [handler, setHandler] = useState<DataLossHandler | null>(null);

  const onCancel = () => {
    handler?.(false);
    setHandler(null);
  };

  const onConfirm = () => {
    handler?.(true);
    setHandler(null);
    dirtyContext.setDirty(false);
  };

  const set = (callback: DataLossHandler | null) => {
    setHandler(() => callback);
  };

  useEffect(() => {
    if (!dirtyContext.isDirty && handler) {
      onConfirm();
    }
  }, [handler, dirtyContext.isDirty]);

  return (
    <DataLossContext.Provider value={{ handler, setHandler: set }}>
      <ConfirmationDialog
        title={t('common.navigation.confirm-unsaved-changes-title')}
        label={t('common.navigation.confirm-unsaved-changes-title')}
        open={dirtyContext.isDirty && !!handler}
        onClose={onCancel}
        onConfirm={onConfirm}
        onCancel={onCancel}
        zIndex="confirmation-dialog"
        description={t('common.navigation.confirm-unsaved-changes')}
      />
      {children}
    </DataLossContext.Provider>
  );
};
